import { CircleAlert, CircleCheck, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ArticleEmbedStateEnum } from '@/Domain/api/KnowledgeBase/dto/EmbedState.dto';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { cn } from '@/Domain/utils/cn';

type Props = {
  embedState?: ArticleEmbedStateEnum;
  className?: string;
};

const CONFIG: Record<
  ArticleEmbedStateEnum,
  { icon: LucideIcon | null; spin: boolean; color: string; labelKey: string }
> = {
  [ArticleEmbedStateEnum.PENDING]: {
    icon: null,
    spin: true,
    color: 'text-muted-foreground',
    labelKey: 'embed_status.pending',
  },
  [ArticleEmbedStateEnum.EMBEDDING]: {
    icon: null,
    spin: true,
    color: 'text-info',
    labelKey: 'embed_status.embedding',
  },
  [ArticleEmbedStateEnum.EMBEDDED]: {
    icon: CircleCheck,
    spin: false,
    color: 'text-success',
    labelKey: 'embed_status.embedded',
  },
  [ArticleEmbedStateEnum.FAILED]: {
    icon: CircleAlert,
    spin: false,
    color: 'text-destructive',
    labelKey: 'embed_status.failed',
  },
};

export const ArticleEmbedStatusBadge = ({ embedState, className }: Props) => {
  const { t } = useTranslation('knowledge_base');

  if (!embedState) return null;

  const config = CONFIG[embedState];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <span
      data-testid="article-embed-status-badge"
      data-embed-state={embedState}
      className={cn('flex items-center gap-1.5 whitespace-nowrap text-xs', config.color, className)}
    >
      {config.spin ? (
        <Spinner className={cn('h-3.5 w-3.5', config.color)} />
      ) : Icon ? (
        <Icon className="h-3.5 w-3.5" />
      ) : null}
      {t(config.labelKey)}
    </span>
  );
};
